import React, { useState, useEffect, useCallback } from 'react';
import { H2, InputGroup, Button, Intent, Card, Spinner } from '@blueprintjs/core';
import TeamMakerCard from '../components/TeamMakerCard';
import { getAllPlayerNames, getMultiplePlayerElos } from '../firebase/playerService';
import { useNavigate } from 'react-router-dom';

function TeamMaker() {
  const navigate = useNavigate();
  const [racerNames, setRacerNames] = useState(Array(8).fill(''));
  const [racerElos, setRacerElos] = useState({});
  const [teams, setTeams] = useState([]);
  const [pickOrder, setPickOrder] = useState([]);
  const [showPickOrder, setShowPickOrder] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isShuffling, setIsShuffling] = useState(false);
  const [availablePlayers, setAvailablePlayers] = useState([]);
  const [loadingElos, setLoadingElos] = useState(false);
  const [animatingCards, setAnimatingCards] = useState([]);

  // Load available players on mount
  useEffect(() => {
    loadAvailablePlayers();
  }, []);

  const loadAvailablePlayers = async () => {
    try {
      const players = await getAllPlayerNames();
      setAvailablePlayers(players);
    } catch (error) {
      console.error('Error loading players:', error);
    }
  };

  // Fetch ELOs when names change
  useEffect(() => {
    const fetchElos = async () => {
      const validNames = racerNames.filter(name => name && name.trim() !== '');
      if (validNames.length > 0) {
        setLoadingElos(true);
        try {
          const elos = await getMultiplePlayerElos(validNames);
          setRacerElos(elos);
        } catch (error) {
          console.error('Error fetching ELOs:', error);
        } finally {
          setLoadingElos(false);
        }
      }
    };

    const timeoutId = setTimeout(fetchElos, 500);
    return () => clearTimeout(timeoutId);
  }, [racerNames]);

  const handleNameChange = (index, value) => {
    const newNames = [...racerNames];
    newNames[index] = value;
    setRacerNames(newNames);
  };

  const generateTeams = useCallback(() => {
    setIsGenerating(true);
    
    // Create player objects with ELO
    const players = racerNames.map((name, index) => ({
      name: name || `Player ${index + 1}`,
      elo: racerElos[name] || 500,
      originalIndex: index
    }));

    // Sort by ELO (highest to lowest)
    players.sort((a, b) => b.elo - a.elo);

    // Snake draft pairing
    const newTeams = [
      { player1: players[0], player2: players[7] }, // Team A: 1st + 8th
      { player1: players[1], player2: players[6] }, // Team B: 2nd + 7th
      { player1: players[2], player2: players[5] }, // Team C: 3rd + 6th
      { player1: players[3], player2: players[4] }, // Team D: 4th + 5th
    ];

    setTeams(newTeams);
    setShowPickOrder(false);
    setPickOrder([]);
    
    setTimeout(() => {
      setIsGenerating(false);
    }, 300);
  }, [racerNames, racerElos]);

  const chooseCharacterOrder = () => {
    setIsShuffling(true);
    setAnimatingCards([0, 1, 2, 3]);
    
    // Create shuffling animation
    let shuffleCount = 0;
    const maxShuffles = 10;
    
    const shuffleInterval = setInterval(() => {
      const tempOrder = [1, 2, 3, 4].sort(() => Math.random() - 0.5);
      setPickOrder(tempOrder);
      shuffleCount++;
      
      if (shuffleCount >= maxShuffles) {
        clearInterval(shuffleInterval);
        
        // Final random order
        const finalOrder = [1, 2, 3, 4].sort(() => Math.random() - 0.5);
        setPickOrder(finalOrder);
        setShowPickOrder(true);
        setIsShuffling(false);
        
        // Stagger the animation of each card
        finalOrder.forEach((_, index) => {
          setTimeout(() => {
            setAnimatingCards(prev => prev.filter(i => i !== index));
          }, index * 100);
        });
      }
    }, 100);
  };

  const reset = () => {
    setRacerNames(Array(8).fill(''));
    setRacerElos({});
    setTeams([]);
    setPickOrder([]);
    setShowPickOrder(false);
    setIsGenerating(false);
    setIsShuffling(false);
    setAnimatingCards([]);
  };

  const allNamesEntered = racerNames.every(name => name && name.trim() !== '');
  const teamsGenerated = teams.length > 0;
  const orderChosen = showPickOrder && pickOrder.length > 0;

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
      <style jsx>{`
        @keyframes spinIn {
          from {
            transform: rotate(0deg) scale(0);
            opacity: 0;
          }
          to {
            transform: rotate(360deg) scale(1);
            opacity: 1;
          }
        }
        
        @keyframes cardShuffle {
          0% { transform: translateY(0) rotateY(0deg); }
          25% { transform: translateY(-10px) rotateY(90deg); }
          50% { transform: translateY(0) rotateY(180deg); }
          75% { transform: translateY(-10px) rotateY(270deg); }
          100% { transform: translateY(0) rotateY(360deg); }
        }
        
        .pick-order-badge.spin-in {
          animation: spinIn 0.5s ease-out;
        }
        
        .team-maker-card.animating {
          animation: cardShuffle 0.4s ease-in-out;
        }
        
        .racer-input-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1rem;
          margin-bottom: 2rem;
        }
        
        .teams-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }
        
        @media (max-width: 768px) {
          .racer-input-grid {
            grid-template-columns: 1fr;
          }
          .teams-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div style={{ marginBottom: '1rem' }}>
        <Button
          minimal
          icon="arrow-left"
          onClick={() => navigate('/other')}
        >
          Back to Other Tools
        </Button>
      </div>

      <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
        <H2 style={{ marginBottom: '0.5rem' }}>Team Maker</H2>
        <p style={{ color: 'var(--text-secondary)' }}>
          Enter 8 racer names to automatically generate balanced teams based on ELO ratings
        </p>
      </div>

      {!teamsGenerated && (
        <Card style={{ padding: '2rem', marginBottom: '2rem' }}>
          <h3 style={{ marginBottom: '1.5rem' }}>Enter Racer Names</h3>
          <div className="racer-input-grid">
            {racerNames.map((name, index) => (
              <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ minWidth: '20px', color: 'var(--text-secondary)' }}>{index + 1}.</span>
                <InputGroup
                  placeholder={`Racer ${index + 1}`}
                  value={name}
                  onChange={(e) => handleNameChange(index, e.target.value)}
                  style={{ flex: 1 }}
                  list={`players-list-${index}`}
                />
                <datalist id={`players-list-${index}`}>
                  {availablePlayers.map((player, i) => (
                    <option key={i} value={player} />
                  ))}
                </datalist>
                <span style={{ 
                  minWidth: '60px', 
                  textAlign: 'right',
                  color: 'var(--accent-primary)',
                  fontSize: '0.9rem',
                  fontWeight: 600
                }}>
                  {loadingElos && name ? (
                    <Spinner size={16} />
                  ) : (
                    name && racerElos[name] ? `${racerElos[name]} ELO` : ''
                  )}
                </span>
              </div>
            ))}
          </div>
          
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <Button
              large
              intent={Intent.PRIMARY}
              onClick={generateTeams}
              disabled={!allNamesEntered || isGenerating}
              icon="people"
            >
              {isGenerating ? 'Generating...' : 'Generate Teams'}
            </Button>
          </div>
        </Card>
      )}

      {teamsGenerated && (
        <>
          <div className="teams-grid">
            {teams.map((team, index) => (
              <TeamMakerCard
                key={index}
                team={team}
                teamLetter={String.fromCharCode(65 + index)} // A, B, C, D
                pickOrder={pickOrder[index]}
                showPickOrder={showPickOrder}
                isAnimating={animatingCards.includes(index)}
              />
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            {!orderChosen ? (
              <Button
                large
                intent={Intent.SUCCESS}
                onClick={chooseCharacterOrder}
                disabled={isShuffling}
                icon="random"
                style={{ marginRight: '1rem' }}
              >
                {isShuffling ? 'Shuffling...' : 'Choose Character Order'}
              </Button>
            ) : (
              <Button
                large
                intent={Intent.PRIMARY}
                onClick={reset}
                icon="refresh"
              >
                Good to Go!
              </Button>
            )}
            
            {!orderChosen && (
              <Button
                large
                onClick={reset}
                icon="undo"
              >
                Start Over
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default TeamMaker;
